import { assertNotNull } from '@/helpers/typing'
import type { DocWithContent } from '@/models/interfaces'

export interface ChapterTiming {
  idx: number
  start: number
  end: number | null
}

/**
 * Convert backend chapter start timestamps from milliseconds into ranges in
 * seconds.
 *
 * @example
 * [0, 1000, 2000] becomes:
 * [
 *   { idx: 0, start: 0, end: 1 },
 *   { idx: 1, start: 1, end: 2 },
 *   { idx: 2, start: 2, end: null },
 * ]
 */
export function chapterTimings(doc: DocWithContent): ChapterTiming[] {
  const timings = assertNotNull(doc.audio).timings
  // Note: Math.floor is needed to make the switch between chapters deterministic,
  // otherwise (no floor), we may randomly miss the chapter start due to floating
  // point errors, for example:
  //
  // - The chapter 4 has the start time: 2099.615 seconds
  // - We click the chapter 4 link in the side menu
  // - We set audio.currentTime to 2099.615 seconds (audio time is in seconds,
  //   but we can use fractional time here)
  // - Now we get into the audio 'timeupdate' event (`_timeUpdateHandler`) and, suddenly,
  //   the audio.currentTime value is 2099.614999 wich is just a bit lower than
  //   the chapter 4 start, so we hit the chapter 3 instead and switch UI to it.
  //
  // At this point we are stuck at chapter 3 and can not switch to chapter 4.
  // Math.floor prevents this by making all timings integer and avoinding floating
  // point issues.
  return doc.content.map((_, idx) => ({
    idx,
    start: Math.floor(timings[idx] / 1000),
    end:
      idx + 1 < doc.content.length ? Math.floor(timings[idx + 1] / 1000) : null,
  }))
}

/** Find the last chapter whose start boundary is at or before `position`. */
export function findChapterAtPosition<T extends { start: number }>(
  chapters: T[],
  position: number,
): T | undefined {
  for (let idx = chapters.length - 1; idx >= 0; idx--) {
    if (chapters[idx].start <= position) {
      return chapters[idx]
    }
  }
}
